import {observer} from 'mobx-react-lite';
import {Link, useLocation} from 'react-router-dom';
import {useService} from "../../../../infrastructure/service/service";
import {AppBar, Box, Button, Divider, Menu, MenuItem, Toolbar, Typography} from '@mui/material';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, {useEffect, useState} from "react";
import {User} from "../../../../entity/model/user";
import UserDialog from "../users/UserDialog";
import LoadingComponent from "../widget/LoadingComponent";
import {ManageMode} from "../../../../entity/enums/manage-mode";

// @ts-ignore
import navLogo from '../../../../assets/images/authentication/nav-logo.png';
import '../../../../assets/css/authentication/nav-bar.css';
import '../../../../assets/css/style.css';

const NavBar = (): JSX.Element => {
  const {userService} = useService();
  const location = useLocation();

  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [selectedButton, setSelectedButton] = useState<string>('/dashboard');
  const [currentUser, setCurrentUser] = useState<User>();
  const [profileDialogOpen, setProfileDialogOpen] = useState<boolean>(false);
  const [isLoggingOut, setLoggingOut] = useState<boolean>(false);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const user = await userService.getCurrentUser();
      setCurrentUser(user);
      setSelectedButton(location.pathname);
    }
    init();
  }, []);

  const handleButtonClick = (path: string): void => {
    setSelectedButton(path);
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuElement(event.currentTarget);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handleLogOut = async (): Promise<void> => {
    handleMenuClose();

    setLoggingOut(true);
    await userService.logout();
    setLoggingOut(false);
  }

  const handleOpenProfileDialog = (): void => {
    handleMenuClose();
    setProfileDialogOpen(true);
  }

  return (
      <>
        <AppBar className={'nav-bar'} position='static' color='transparent' elevation={0}>
          <Toolbar className={'nav-toolbar'}>
            <Link id={'nav-brand'} to={'/'}>
              <Box className='nav-logo' component='img' src={navLogo} alt='Zitles Logo'/>
            </Link>
            <Box className={'nav-btn-group'}>
              <Button
                  id={'nav-dashboard'}
                  color='inherit'
                  disableRipple
                  disableFocusRipple
                  className={selectedButton === '/dashboard' ? 'nav-btn-selected' : 'nav-btn'}
                  component={Link}
                  to='/dashboard'
                  onClick={(): void => handleButtonClick('/dashboard')}>
                DASHBOARD
              </Button>
              <Button
                  id={'nav-title-bank'}
                  color='inherit'
                  disableRipple
                  disableFocusRipple
                  className={selectedButton === '/title-bank' ? 'nav-btn-selected' : 'nav-btn'}
                  component={Link}
                  to='/title-bank'
                  onClick={() => handleButtonClick('/title-bank')}>
                TITLE BANK
              </Button>
              <Button
                  id={'nav-marketplace'}
                  color='inherit'
                  disableRipple
                  disableFocusRipple
                  className={selectedButton === '/marketplace' ? 'nav-btn-selected' : 'nav-btn'}
                  component={Link}
                  to='/marketplace'
                  onClick={() => handleButtonClick('/marketplace')}>
                MARKETPLACE
              </Button>
              {
                userService.isAdminUser() &&
                  <Button
                      id={'nav-users'}
                      color='inherit'
                      disableRipple
                      disableFocusRipple
                      className={selectedButton === '/users' ? 'nav-btn-selected' : 'nav-btn'}
                      component={Link}
                      to='/users'
                      onClick={() => handleButtonClick('/users')}>
                    USERS
                  </Button>
              }
              <Button
                  id={'nav-my-profile'}
                  color='inherit'
                  disableRipple
                  disableFocusRipple
                  className={'nav-btn'}
                  onClick={handleMenuOpen}>
                MY PROFILE <KeyboardArrowDownIcon/>
              </Button>
              <Menu className={'zitles-menu'} anchorEl={menuElement} open={Boolean(menuElement)} onClose={handleMenuClose}>
                <MenuItem disableRipple className={'zitles-menu-item'}>
                  <Box className={'profile-menu-header'}>
                    <Typography variant="subtitle1" className={'profile-menu-username'}>
                      {currentUser ? currentUser.firstName + ' ' + currentUser.lastName : ''}
                    </Typography>
                    <Typography variant="body2" className={'profile-menu-email'}>
                      {currentUser ? currentUser.email : ''}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider/>
                <MenuItem id={'menu-profile'} disableRipple className={'zitles-menu-item'} onClick={handleOpenProfileDialog}>
                  <AccountCircleIcon/>
                  <Typography className={'zitles-menu-item button'}>My Profile</Typography>
                </MenuItem>
                {
                    userService.isAdminUser() &&
                      <MenuItem id={'menu-subscription'} disableRipple className={'zitles-menu-item'} component={Link} to={'/subscription'} onClick={handleMenuClose}>
                        <SubscriptionsIcon/>
                        <Typography className={'zitles-menu-item button'}>Subscription</Typography>
                      </MenuItem>
                }
                {
                  userService.isSuperAdminUser() &&
                    <MenuItem id={'menu-marketplace-management'} disableRipple className={'zitles-menu-item'} component={Link} to={'/marketplace-management'} onClick={handleMenuClose}>
                      <SettingsIcon/>
                      <Typography className={'zitles-menu-item button'}>Settings</Typography>
                    </MenuItem>
                }
                <Divider/>
                <MenuItem id={'menu-logout'} disableRipple className={'zitles-menu-item'} onClick={handleLogOut}>
                  <LogoutIcon/>
                  <Typography className={'zitles-menu-item button'}>Log Out</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        {
          profileDialogOpen &&
            <UserDialog
                open={profileDialogOpen}
                setOpen={setProfileDialogOpen}
                mode={ManageMode.PROFILE}
                user={currentUser}/>
        }
        {
          isLoggingOut &&
            <LoadingComponent title={'Logging Out...'}/>
        }
      </>
  );
}

export default observer(NavBar);
